import { ChangeEvent, useCallback, useMemo, useState } from 'react';
import { noop } from 'lodash-es';
import { SearchInput } from '@ds';
import { FilterDropdown } from '@ui';
import { useHubResourcesList } from '@lp/features/RoutePlanner/hooks/useHubResourcesList';
import { DeviceTable } from '@lp/features/AssignmentTables/DeviceTable/DeviceTable';
import { getFilterTitle } from '@lp/features/CaseOverview/components/Tabs/AssignedResources/components/AssignResourcesModal/components/CaseResourceFilters/utils';
import { useRouteDetails } from '@lp/features/RoutePlanner/hooks/useRouteDetails';
import { useRouteResources } from '@lp/features/RoutePlanner/hooks/useRouteResources';
import { OrDateTimePicker } from '@lp/features/CaseOverview/components/Tabs/AssignedResources/components/AssignResourcesModal/components';
import { getTimeZonedDate } from '@/utils/helpers';
import { CaseResourceType, OrganType } from '@/api/apis/case';
import { OcsConsoleData, ResourceStatus } from '@/api/apis/resources';
import { LegLocationTypeEnum, RouteTypeEnum } from '@/api/apis/route';
import { useCaseDetails } from '@/hooks/useCaseDetails';

export type RoutePlannerDeviceListProps = {
  organType: OrganType;
  selectedHubId?: string;
  showHubDropdown?: boolean;
  selectedResourceId?: string;
  isSwap?: boolean;
  onSelect: (data: OcsConsoleData, isOCSConsole: boolean) => void;
};

export const RoutePlannerDeviceList = ({
  organType,
  selectedHubId,
  onSelect,
  showHubDropdown = true,
  selectedResourceId,
  isSwap = false,
}: RoutePlannerDeviceListProps) => {
  const { legs } = useRouteDetails(RouteTypeEnum.Main);
  const { ClinicalSpecialist: specialists } = useRouteResources();
  const { caseId, donorHospitalDate, donorHospitalTime, donorHospitalTimeZone, resources, trialProgram } =
    useCaseDetails();
  const orCaseDateTime = getTimeZonedDate(donorHospitalDate, donorHospitalTime, donorHospitalTimeZone);
  const [searchValue, setSearchValue] = useState('');

  const defaultHubId = useMemo(() => {
    if (selectedHubId) return selectedHubId;

    const legStartHub = legs.find((s) => s.departureLocation.locationType === LegLocationTypeEnum.hub);

    if (
      legStartHub?.departureLocation.locationType === LegLocationTypeEnum.hub &&
      legStartHub?.departureLocation.locationId
    ) {
      return legStartHub?.departureLocation.id;
    }

    if (specialists[0]?.data?.baseLocation?.id) {
      return specialists[0]?.data?.baseLocation?.id;
    }

    return '';
  }, [selectedHubId, legs, specialists]);

  const { hubSelectList, onHubSelectChange, selectedHub } = useHubResourcesList({
    defaultHubId,
    isClosestHubCalculate: !selectedHubId,
    fetchResources: false,
  });

  const handleReserve = useCallback(
    (data: OcsConsoleData) => {
      onSelect(data, true);
    },
    [onSelect],
  );

  const onSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value);
  };

  const handleRowClick = (item: OcsConsoleData) => {
    const isAvailable = item.reservations.every((r) =>
      [ResourceStatus.Done, ResourceStatus.Canceled].includes(r.status),
    );
    if (item && isAvailable) {
      handleReserve(item);
    }
  };

  const assignedDevices = resources.filter((d) => d.resourceType === CaseResourceType.OCSConsole);

  return (
    <div className="flex flex-col place-items-stretch gap-6">
      {showHubDropdown && (
        <div className="flex items-center justify-between gap-6 min-w-[50%]">
          {!isSwap ? (
            <div className="flex gap-10 items-center">
              <FilterDropdown
                isFaded
                title={selectedHub?.id ? getFilterTitle(hubSelectList, selectedHub.id) : 'NOP Hub'}
                items={hubSelectList}
                selectedKeys={selectedHub?.id ? [selectedHub.id] : []}
                mode="single"
                showCounter={false}
                onSelectedKeysChange={(values) => onHubSelectChange(values[0])}
              />
              <OrDateTimePicker
                showOrTypeSwitch={false}
                orDateTime={orCaseDateTime!}
                onOrDateTimeChange={noop}
                isCustom={false}
                onIsCustomChange={noop}
              />
            </div>
          ) : (
            <div className="flex gap-10 items-center">
              <span className="text-m-m">
                {selectedHub?.id ? `HUB: ${getFilterTitle(hubSelectList, selectedHub.id)}` : 'HUB:'}
              </span>
              <OrDateTimePicker
                showOrTypeSwitch={false}
                orDateTime={orCaseDateTime!}
                onOrDateTimeChange={noop}
                isCustom={false}
                onIsCustomChange={noop}
              />
            </div>
          )}
        </div>
      )}
      <SearchInput placeholder="Search" value={searchValue} onChange={onSearchChange} />
      <div className="flex flex-col">
        <DeviceTable
          caseId={caseId}
          searchTerm={searchValue}
          isLogistics
          hubId={selectedHub?.id}
          assignedDevices={assignedDevices}
          selectedDeviceId={selectedResourceId || ''}
          organType={organType}
          onDeviceAssign={handleRowClick}
          isSwap={isSwap}
          trialProgram={trialProgram}
        />
      </div>
    </div>
  );
};
